import { Badge, Button, Card, CardContent, Chip, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { ChevronRight, Settings, Sync } from "@mui/icons-material";
import { SchoolConfigDialog } from "./SchoolConfigDialog";
import { formatDistanceToNow } from "date-fns";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import GeneralLoading from "../../../shared-components/Loading";
import { useNavigate } from "react-router-dom";

const RequestCard = ({
  request,
  needsAction,
  inProgress,
  completed,
  onConfigureClicked,
  onStartSyncClicked,
}) => {
  const navigate = useNavigate();
  const schoolIsConfigured =
    request.school.emailDomains?.length > 0 &&
    !!request.school.subscriptionStatus;
  const getChipColor = (request) => {
    switch (request.syncStatus) {
      case "Request approved":
        return "success";
      case "Request declined":
        return "error";
      default:
        return "default"; // normal color
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="column" spacing={0.5}>
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <Typography variant="subtitle1" fontWeight="bold">
                {request.school.name}
              </Typography>
              {!inProgress && (
                <Chip
                  label={request.syncStatus}
                  color={getChipColor(request)}
                />
              )}
            </Stack>
            <Typography variant="body2">
              Last updated{" "}
              {formatDistanceToNow(new Date(request.lastUpdated), {
                addSuffix: true,
              })}
            </Typography>
          </Stack>
          {inProgress ? (
            <GeneralLoading
              message={"Syncing..."}
              direction={"row"}
              size={30}
            />
          ) : (
            <Stack direction={"row"} spacing={2}>
              {!completed && (
                <>
                  {" "}
                  <Badge
                    color="error"
                    badgeContent={!schoolIsConfigured ? "!" : null}
                  >
                    <Button
                      variant="contained"
                      endIcon={<Settings />}
                      onClick={() => onConfigureClicked(request.school)}
                    >
                      Configure
                    </Button>
                  </Badge>
                  {needsAction && (
                    <Button
                      variant="contained"
                      endIcon={<Sync />}
                      onClick={() => onStartSyncClicked(request.school)}
                      disabled={
                        !schoolIsConfigured ||
                        request.syncStatus === "Request Declined"
                      }
                    >
                      Sync
                    </Button>
                  )}
                </>
              )}
              <Button
                variant="contained"
                endIcon={<ChevronRight />}
                onClick={() =>
                  navigate(`/superuser/schools/${request.school.id}`)
                }
              >
                View school
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export const SyncRequestsList = ({
  requests,
  onRequestsChanged = () => {},
  needsAction = false,
  inProgress = false,
  completed = false,
  dataTestId = "sync-request-list",
}) => {
  const [school, setSchool] = useState(null);
  const [open, setOpen] = useState(false);

  const handleConfigureClicked = (school) => {
    setSchool(school);
    setOpen(true);
  };

  const ApiClient = useApiClient();

  const handleStartSyncClicked = async (school) => {
    try {
      const response = await ApiClient.wonde.startSchoolSync({
        school: school.id,
      });
      if (response.success) {
        enqueueSnackbar("Successfully started sync", {
          variant: "success",
          autoHideDuration: 3000,
        });
        onRequestsChanged();
      } else {
        enqueueSnackbar("Problem starting sync", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to start sync", error);
    }
  };

  return (
    <>
      <SchoolConfigDialog open={open} setOpen={setOpen} school={school} />
      <Stack direction={"column"} spacing={2} data-testid={dataTestId}>
        {requests.map((req, index) => (
          <RequestCard
            key={index}
            request={req}
            onConfigureClicked={handleConfigureClicked}
            onStartSyncClicked={handleStartSyncClicked}
            needsAction={needsAction}
            inProgress={inProgress}
            completed={completed}
          />
        ))}
      </Stack>
    </>
  );
};
